/**
 * The topic list row — shared by the chapter's topic list and the favorites
 * screen, so the two lists can never drift apart. Both take the same
 * `topics_head` row shape (`id`, `title`, `intro`, `imageBlob`, `needUpdate`)
 * plus a `done` flag, which is **not** on that row: completion lives in its own
 * store, so the list reads the set of finished ids once and tells each row where
 * it stands (see `study/completion.js`).
 *
 * Both variants sit on the same `cream` surface with the same `rounded-2xl` and
 * the same `sand/25` press, matching the chapter cards on home — one row, one
 * look, whichever list it lands in.
 */

import { escapeHtml, hashHref } from '../utils/html.js';
import { showBlobImage } from '../utils/blobImage.js';
import { ICONS } from './icons.js';

/**
 * A single topic row: the cached image thumbnail, the title and its intro, and a
 * "content updated" hint when flagged, with a green check overhanging the
 * thumbnail's bottom-right corner once the topic is done. The whole row links
 * into the topic detail screen (`#/topic/:id`). The `<img>`
 * starts blank — its source is wired from the stored Blob by
 * {@link wireThumbnails} after the row is in the DOM.
 *
 * A topic with no cached `imageBlob` renders **no thumbnail column** (not an
 * empty tinted square), so its text starts at the row's left edge and runs the full
 * width — and its "done" check falls back to the row's top-right corner, having
 * no image to hang off.
 *
 * @param {object} topic A `topics_head` row.
 * @param {{ favorite?: boolean, done?: boolean }} [options]
 *   - favorite: render the trailing slot as a filled-heart button
 *     (`[data-favorite-toggle="<id>"]`) instead of the chevron, so the
 *     favorites list can un-star a topic without leaving the screen. The button
 *     is a sibling of the `<a>`, never nested inside it.
 *   - done: this topic is in `topics_done` → show the green check.
 */
export function topicRow(topic, { favorite = false, done = false } = {}) {
  const id = escapeHtml(topic.id);

  const intro = topic.intro
    ? `<p class="mt-0.5 truncate text-sm leading-snug text-neutral-600">${escapeHtml(topic.intro)}</p>`
    : '';

  const updateHint = topic.needUpdate
    ? `<span class="mt-1 inline-flex items-center gap-1 text-xs font-medium text-amber-600">
         <span class="h-1.5 w-1.5 rounded-full bg-amber-500"></span>Dostępna aktualizacja
       </span>`
    : '';

  // A topic with no cached thumbnail gets **no thumbnail column at all** — the
  // title starts at the row's left edge instead of being indented past an empty
  // tinted square.
  const hasThumb = Boolean(topic.imageBlob);

  // Completion hangs off the **thumbnail's** bottom-right corner, overhanging it
  // slightly. Every topic ships an image, so that is one position on every row.
  // The ring is `cream` because that is what the badge overhangs onto: the image
  // edge and the row behind it, and the row is cream in both lists.
  // `pointer-events-none` keeps it from swallowing taps meant for the row / the
  // un-star button it sits near.
  const doneBadge = (placement) =>
    done
      ? `<span role="img" aria-label="Ukończono"
           class="pointer-events-none absolute ${placement} z-10 grid h-5 w-5 place-items-center rounded-full bg-green-600 text-white ring-2 ring-cream">
           ${ICONS.check('h-3 w-3')}
         </span>`
      : '';

  // `self-start` keeps the thumbnail pinned to the top of the row while the
  // title / chevron stay vertically centred; `relative` is what the check hangs
  // off.
  const thumb = hasThumb
    ? `<div class="relative h-12 w-12 shrink-0 self-start">
          <img data-topic-thumb="${id}" alt="" loading="lazy"
            class="h-full w-full rounded-sm bg-mist object-cover" />
          ${doneBadge('-bottom-1 -right-1')}
        </div>`
    : '';

  // Fallback for a row whose thumbnail never cached (a swallowed media fetch):
  // the check keeps the row's top-right corner, anchored to the `<li>` — the one
  // element both layouts share as the full-width row, unlike the `<a>`, whose
  // right edge in the favorites layout stops before the heart.
  const status = hasThumb ? '' : doneBadge('right-0 top-0');

  const body = `
        ${thumb}
        <div class="min-w-0 flex-1">
          <h3 class="text-base font-semibold text-neutral-900">${escapeHtml(topic.title)}</h3>
          ${intro}
          ${updateHint}
        </div>`;

  if (favorite) {
    return `
    <li data-topic-row="${id}" class="relative flex items-center rounded-2xl bg-cream pr-1.5">
      <a href="${hashHref('/topic/{}', topic.id)}"
        class="flex min-w-0 flex-1 items-center gap-3 rounded-2xl p-2 transition active:bg-sand/25">
        ${body}
      </a>
      <button type="button" data-favorite-toggle="${id}" aria-pressed="true" aria-label="Usuń z ulubionych"
        class="grid h-9 w-9 shrink-0 place-items-center rounded-full text-red-500 transition hover:bg-red-50 active:scale-95">
        ${ICONS.heartFilled('h-5 w-5')}
      </button>
      ${status}
    </li>`;
  }

  return `
    <li data-topic-row="${id}" class="relative">
      <a href="${hashHref('/topic/{}', topic.id)}"
        class="flex items-center gap-3 rounded-2xl bg-cream px-4 py-3 transition active:bg-sand/25">
        ${body}
        <span class="shrink-0 text-brand/40">${ICONS.chevronRight()}</span>
      </a>
      ${status}
    </li>`;
}

/**
 * Point each thumbnail `<img>` at its topic's cached Blob. The image is read only
 * from IndexedDB — the original http URL is never requested at render time.
 */
export function wireThumbnails(container, topics) {
  const byId = new Map(topics.map((topic) => [topic.id, topic]));
  container.querySelectorAll('img[data-topic-thumb]').forEach((img) => {
    showBlobImage(img, byId.get(img.getAttribute('data-topic-thumb'))?.imageBlob);
  });
}
